import { useState, useEffect } from "react";
import * as api from "../../apiService";

const STATUS_CFG = {
  APPROVED:             { bg:"#d1fae5", color:"#065f46", border:"#6ee7b7", label:"Approved" },
  NEED_FURTHER_STUDY:   { bg:"#fef9c3", color:"#854d0e", border:"#fde047", label:"Need Further Study" },
  UNDER_OBSERVATION:    { bg:"#e0f2fe", color:"#0369a1", border:"#7dd3fc", label:"Under Observation" },
  REJECTED:             { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label:"Rejected" },
};

const STATUSES = [
  { value:"UNDER_OBSERVATION",  label:"Under Observation" },
  { value:"APPROVED",           label:"Approved" },
  { value:"NEED_FURTHER_STUDY", label:"Need Further Study" },
  { value:"REJECTED",           label:"Rejected" },
];

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] ?? { bg:"#f3f4f6", color:"#6b7280", border:"#d1d5db", label: status };
  return (
    <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`,
      borderRadius:4, padding:"2px 10px", fontSize:12, fontWeight:600 }}>
      {c.label}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase",
        letterSpacing:".05em", marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:13, color:"#111" }}>{value || <span style={{ color:"#bbb" }}>—</span>}</div>
    </div>
  );
}

export function RmEvaluationDetailModal({ evaluation, rms = [], canEdit, canDelete, onUpdate, onDelete, onClose }) {
  const [ev,         setEv]         = useState(evaluation);
  const [editing,    setEditing]    = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [error,      setError]      = useState(null);

  // Edit form state
  const [editStatus,  setEditStatus]  = useState(ev.status);
  const [editSupplier,setEditSupplier]= useState(ev.supplier || "");
  const [editPurpose, setEditPurpose] = useState(ev.purpose  || "");
  const [editRemarks, setEditRemarks] = useState(ev.remarks  || "");
  const [editParams,  setEditParams]  = useState(
    (ev.params ?? []).map(p => ({
      parameter: p.parameter,
      specValue: p.specValue || "",
      measured:  p.measured  || "",
      uom:       p.uom       || "",
    }))
  );
  const [saving, setSaving] = useState(false);

  // Load full detail (with params) when modal opens
  useEffect(() => {
    api.fetchRmEvalDetail(evaluation.id)
      .then(detail => {
        setEv(detail);
        setEditStatus(detail.status);
        setEditSupplier(detail.supplier || "");
        setEditPurpose(detail.purpose  || "");
        setEditRemarks(detail.remarks  || "");
        setEditParams((detail.params ?? []).map(p => ({
          parameter: p.parameter,
          specValue: p.specValue || "",
          measured:  p.measured  || "",
          uom:       p.uom       || "",
        })));
      })
      .catch(() => {});
  }, [evaluation.id]);

  function addParam() {
    setEditParams(p => [...p, { parameter:"", specValue:"", measured:"", uom:"" }]);
  }

  function removeParam(i) {
    setEditParams(p => p.filter((_, idx) => idx !== i));
  }

  function setParamField(i, field, value) {
    setEditParams(p => p.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const body = {
        evalType: ev.evalType,
        rmId:     ev.rmId,
        rmName:   ev.rmName,
        rmCode:   ev.rmCode,
        status:   editStatus,
        date:     ev.date,
        supplier: editSupplier.trim() || null,
        purpose:  editPurpose.trim()  || null,
        remarks:  editRemarks.trim()  || null,
        params:   editParams
          .filter(p => p.parameter.trim())
          .map(p => ({
            parameter: p.parameter.trim(),
            specValue: p.specValue.trim() || null,
            measured:  p.measured.trim()  || null,
            uom:       p.uom.trim()       || null,
          })),
      };
      const updated = await onUpdate(ev.id, body);
      setEv(updated);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete evaluation ${ev.evalNo}? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(ev.id);
      onClose();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  }

  const rmLabel = ev.evalType === "ALTERNATE"
    ? `${ev.rmId}${rms.find(r => r.id === ev.rmId)?.name ? " — " + rms.find(r => r.id === ev.rmId).name : ""}`
    : `${ev.rmCode ? ev.rmCode + " — " : ""}${ev.rmName || "—"}`;

  return (
    <div className="modal wide" style={{ maxWidth:720 }}>
      <div className="modal-header">
        <div>
          <h3 className="modal-title" style={{ marginBottom:2 }}>{ev.evalNo}</h3>
          <StatusBadge status={ev.status} />
        </div>
        <button className="btn" onClick={onClose}>✕</button>
      </div>

      {!editing ? (
        <>
          {/* Info Grid */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 24px", marginBottom:12 }}>
            <Field label="Type" value={ev.evalType === "ALTERNATE" ? "Alternate RM" : "New RM"} />
            <Field label="Date" value={ev.date} />
            <Field label={ev.evalType === "ALTERNATE" ? "Raw Material" : "Product"} value={rmLabel} />
            <Field label="Supplier"  value={ev.supplier} />
            <Field label="Purpose"   value={ev.purpose} />
            <Field label="Created By" value={`${ev.createdBy} · ${ev.createdAt?.slice(0,10) || ""}`} />
          </div>
          {ev.remarks && (
            <div style={{ marginBottom:12, fontSize:12, color:"#6b7280",
              background:"#f9fafb", borderRadius:4, padding:"6px 10px" }}>
              {ev.remarks}
            </div>
          )}

          <div className="form-sep" />

          {/* Parameters table */}
          <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase",
            letterSpacing:".05em", marginBottom:8 }}>Test Parameters</div>

          {(ev.params ?? []).length === 0 ? (
            <div style={{ color:"#aaa", fontSize:12, marginBottom:12 }}>No parameters recorded yet.</div>
          ) : (
            <div className="table-wrap" style={{ marginBottom:12 }}>
              <table className="data-table" style={{ fontSize:12 }}>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Spec / Expected</th>
                    <th>Measured</th>
                    <th>UoM</th>
                  </tr>
                </thead>
                <tbody>
                  {(ev.params ?? []).map((p, i) => (
                    <tr key={i}>
                      <td className="fw-600">{p.parameter}</td>
                      <td style={{ color:"#6b7280" }}>{p.specValue || "—"}</td>
                      <td>{p.measured || "—"}</td>
                      <td style={{ color:"#6b7280" }}>{p.uom || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {error && <div className="alert-warn" style={{ marginBottom:8 }}>{error}</div>}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            {canDelete && (
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}
            {canEdit && (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                Update / Add Analysis
              </button>
            )}
          </div>
        </>
      ) : (
        /* ── Edit Mode ── */
        <form onSubmit={handleSave}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 24px" }}>
            <div className="form-row" style={{ gridColumn:"1/-1" }}>
              <span className="form-label">Status</span>
              <select className="form-select" value={editStatus}
                onChange={e => setEditStatus(e.target.value)} style={{ width:220 }}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-row">
              <span className="form-label">Supplier</span>
              <input className="form-input" value={editSupplier}
                onChange={e => setEditSupplier(e.target.value)} style={{ flex:1 }} />
            </div>
            <div className="form-row">
              <span className="form-label">Purpose</span>
              <input className="form-input" value={editPurpose}
                onChange={e => setEditPurpose(e.target.value)} style={{ flex:1 }} />
            </div>
            <div className="form-row" style={{ gridColumn:"1/-1" }}>
              <span className="form-label">Remarks</span>
              <textarea className="form-input" rows={2} value={editRemarks}
                onChange={e => setEditRemarks(e.target.value)}
                style={{ flex:1, resize:"vertical" }} />
            </div>
          </div>

          <div className="form-sep" />
          <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase",
            letterSpacing:".05em", marginBottom:8 }}>Test Parameters</div>

          <div style={{ marginBottom:4, display:"grid",
            gridTemplateColumns:"2fr 2fr 2fr 1fr auto", gap:4,
            fontSize:10, color:"#999", fontWeight:700, textTransform:"uppercase" }}>
            <span>Parameter</span><span>Spec / Expected</span>
            <span>Measured</span><span>UoM</span><span />
          </div>

          {editParams.map((row, i) => (
            <div key={i} style={{ display:"grid",
              gridTemplateColumns:"2fr 2fr 2fr 1fr auto", gap:4, marginBottom:4 }}>
              <input className="form-input" placeholder="Parameter"
                value={row.parameter} onChange={e => setParamField(i, "parameter", e.target.value)} />
              <input className="form-input" placeholder="Spec value"
                value={row.specValue} onChange={e => setParamField(i, "specValue", e.target.value)} />
              <input className="form-input" placeholder="Measured"
                value={row.measured} onChange={e => setParamField(i, "measured", e.target.value)} />
              <input className="form-input" placeholder="UoM"
                value={row.uom} onChange={e => setParamField(i, "uom", e.target.value)} />
              <button type="button" className="btn-small btn-danger"
                onClick={() => removeParam(i)}>✕</button>
            </div>
          ))}
          <button type="button" className="btn" style={{ fontSize:12, marginTop:2 }}
            onClick={addParam}>+ Add Parameter</button>

          {error && <div className="alert-warn" style={{ marginTop:8 }}>{error}</div>}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
            <button type="button" className="btn" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
